import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { Button } from '@openx/components-core/lib/Button/Button.tsx';
import { TextField } from '@openx/components-core/lib/TextField/TextField.tsx';
import {
  FormTypes,
  type FormTypesKey,
  InventoryListFormFields,
  type InventoryListFormState,
  type ListData,
} from '@openx/types/inventoryList.ts';
import { bigNumberWithCommas } from '@openx/utils/lib/bigNumberWithCommas.ts';
import { type ChangeEvent, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { inputListTypesLabels, textareaPlaceholder } from '../../../../constants.tsx';

const textareaStyles = {
  mt: -2,
  height: '520px',
  '.MuiInput-root': {
    height: '100%',
  },
  '.MuiInput-root textarea': {
    height: '100% !important',
    overflow: 'auto !important',
  },
};

type InventoryListTextareaProps = {
  type: FormTypesKey;
  typeLimits: Record<FormTypesKey, number>;
  bulkInput: string;
  validItems: string[];
  listData: ListData[];
  handleBulkValidation: () => void;
  handleBulkAdd: () => void;
  handleInputChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
};

export const InventoryListTextarea = ({
  type,
  typeLimits,
  bulkInput,
  validItems,
  listData,
  handleBulkValidation,
  handleBulkAdd,
  handleInputChange,
}: InventoryListTextareaProps) => {
  const { t } = useTranslation();
  const { setError, getValues } = useFormContext<InventoryListFormState>();
  const placeholder = textareaPlaceholder();
  const listType = inputListTypesLabels();

  const validInputText = useMemo(() => validItems.join('\n'), [validItems]);

  const hasValidItems = validItems.length > 0;

  const { isLimitReached, helperText } = useMemo(() => {
    const currentItemsCount = validItems.length + listData.length;
    const isLimitReached = currentItemsCount > typeLimits[type];

    const errorTexts = {
      [FormTypes.BUNDLES]: t('Limit exceeded: {items} / {limit}', {
        items: bigNumberWithCommas(currentItemsCount),
        limit: bigNumberWithCommas(typeLimits[FormTypes.BUNDLES]),
      }),
      [FormTypes.DOMAINS]: t('Limit exceeded: {items} / {limit}', {
        items: bigNumberWithCommas(currentItemsCount),
        limit: bigNumberWithCommas(typeLimits[FormTypes.DOMAINS]),
      }),
    };

    const helpTexts = {
      [FormTypes.BUNDLES]: t('Enter up to {limit} App Bundle IDs.', {
        limit: bigNumberWithCommas(typeLimits[FormTypes.BUNDLES]),
      }),
      [FormTypes.DOMAINS]: t('Enter up to {limit} domains, subdomains, or specific page URLs.', {
        limit: bigNumberWithCommas(typeLimits[FormTypes.DOMAINS]),
      }),
    };

    return {
      isLimitReached: isLimitReached,
      helperText: isLimitReached ? errorTexts[type] : helpTexts[type],
    };
  }, [typeLimits, type, validItems.length, listData.length, t]);

  const hasInputChanged = bulkInput !== validInputText;
  const addButtonDisabled = !bulkInput || !hasValidItems || hasInputChanged || isLimitReached;

  return (
    <Grid size={6}>
      <Box sx={{ position: 'relative' }}>
        <TextField
          multiline
          sx={textareaStyles}
          label={listType[type]}
          data-test="data-add-section"
          fullWidth
          placeholder={placeholder}
          onChange={event => handleInputChange(event)}
          onBlur={event => {
            event.target.value.trim() === '' &&
              getValues(InventoryListFormFields.VALUES).length === 0 &&
              setError(InventoryListFormFields.VALUES, { message: t('Please enter at least one item') });
          }}
          value={bulkInput}
          minRows={1}
          slotProps={{
            htmlInput: {
              'data-test': 'textarea-input',
            },
          }}
          error={isLimitReached}
          helperText={helperText}
        />
      </Box>

      <Box sx={{ marginTop: 1 }} data-test="transfer-list-bulk-actions">
        <Button disabled={!bulkInput} onClick={handleBulkValidation} data-test="validate">
          {t('Validate')}
        </Button>
        <Button
          sx={{ marginLeft: 1 }}
          disabled={addButtonDisabled}
          onClick={handleBulkAdd}
          color="primary"
          data-test="add"
        >
          {t('Add')}
        </Button>
      </Box>
    </Grid>
  );
};
